import { useState } from 'react'
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom'
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function ResetPassword() {
  const { token } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match')
      toast.error('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long')
      toast.error('Password must be at least 6 characters long')
      return
    }

    setLoading(true)
    setErrorMessage('')

    try {
      const params = new URLSearchParams(location.search)
      const websiteId = params.get('websiteId')
      const websiteSlug = params.get('websiteSlug')
      const isCustomerReset = Boolean(websiteId)

      const endpoint = isCustomerReset
        ? `/customer-auth/reset-password/${encodeURIComponent(token || '')}`
        : `/auth/reset-password/${encodeURIComponent(token || '')}`
      const payload = isCustomerReset ? {
        websiteId,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      } : {
        password: formData.password,
        confirmPassword: formData.confirmPassword
      }

      await axios.post(endpoint, payload)

      setSuccess(true)
      toast.success('Password reset successfully!')

      const destination = websiteSlug ? `/store/${encodeURIComponent(websiteSlug)}` : '/login'

      setTimeout(() => {
        navigate(destination)
      }, 1500)
    } catch (error) {
      const message = error.response?.data?.message || 'Error resetting password'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-xs sm:max-w-md w-full bg-white rounded-xl shadow-lg p-4 sm:p-8 text-center">
          <FiCheckCircle className="w-12 sm:w-16 h-12 sm:h-16 text-success-500 mx-auto mb-4 sm:mb-6" />
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
            Password Reset Successfully!
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 mb-6 sm:mb-8">
            Your password has been updated. You will be redirected to the login page shortly.
          </p>
          <Link to="/login" className="btn-primary inline-block px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base">
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-xs sm:max-w-md w-full bg-white rounded-xl shadow-lg p-4 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <FiLock className="w-6 sm:w-8 h-6 sm:h-8 text-primary-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-xs sm:text-sm text-gray-600">Enter your new password below</p>
        </div>

        {errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiLock className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                className="input-field pl-10 pr-10 text-sm sm:text-base py-2 sm:py-3"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <FiEyeOff className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <FiEye className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiLock className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input-field pl-10 pr-10 text-sm sm:text-base py-2 sm:py-3"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <FiEyeOff className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <FiEye className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 py-2 sm:py-3 text-sm sm:text-base"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Resetting...</span>
              </>
            ) : (
              <span>Reset Password</span>
            )}
          </button>
        </form>

        <div className="mt-4 sm:mt-6 text-center">
          <Link to="/login" className="text-xs sm:text-sm text-primary-600 hover:text-primary-700">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}